import { RefObject, useEffect } from 'react';

import { useTheme } from '@mui/material';

const useDataGridScrollbars = (
  containerRef: RefObject<HTMLElement | null>,
  dataGridKey?: string | number,
  enabled = true,
) => {
  const theme = useTheme();

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const thumbColor = theme.palette.scrollbar.thumb;
    const trackColor = theme.palette.scrollbar.track;

    let teardown: (() => void) | undefined;

    const init = () => {
      const scroller = container.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller');
      const scrollerContent = scroller?.querySelector<HTMLElement>(
        '.MuiDataGrid-virtualScrollerContent',
      );
      if (!scroller || !scrollerContent) return false;

      const TRACK_HEIGHT = 6;
      const THUMB_HEIGHT = 6;
      const CLIP_HEIGHT = THUMB_HEIGHT;
      const TRACK_PADDING = 10;

      const originalPosition = getComputedStyle(container).position;
      if (originalPosition === 'static') {
        container.style.position = 'relative';
      }

      const clipWrapper = document.createElement('div');
      Object.assign(clipWrapper.style, {
        position: 'absolute',
        height: `${CLIP_HEIGHT}px`,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: '10',
      });

      const track = document.createElement('div');
      Object.assign(track.style, {
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        height: `${TRACK_HEIGHT}px`,
        backgroundColor: trackColor,
        borderRadius: '28px',
        boxShadow: 'inset 1px 1px 1.5px 0 rgba(0, 0, 0, 0.15)',
        opacity: '1',
        transition: 'none',
        pointerEvents: 'auto',
      });

      const thumb = document.createElement('div');
      Object.assign(thumb.style, {
        position: 'absolute',
        top: '0',
        height: `${THUMB_HEIGHT}px`,
        minWidth: '20px',
        backgroundColor: thumbColor,
        borderRadius: '20px',
        cursor: 'grab',
        opacity: '1',
        transition: 'none',
      });

      track.appendChild(thumb);
      clipWrapper.appendChild(track);
      container.appendChild(clipWrapper);

      const syncPosition = () => {
        const containerRect = container.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        const top = scrollerRect.bottom - containerRect.top - CLIP_HEIGHT;
        const left = scrollerRect.left - containerRect.left + TRACK_PADDING;
        const right = containerRect.right - scrollerRect.right + TRACK_PADDING;
        clipWrapper.style.top = `${top}px`;
        clipWrapper.style.left = `${left}px`;
        clipWrapper.style.right = `${right}px`;
      };

      const updateThumb = () => {
        syncPosition();
        const scrollWidth = scroller.scrollWidth;
        const clientWidth = scroller.clientWidth;
        const hasOverflow = scrollWidth - clientWidth > 1;
        clipWrapper.style.display = hasOverflow ? '' : 'none';
        if (!hasOverflow) return;
        const ratio = Math.min(1, clientWidth / scrollWidth);
        const trackWidth = track.clientWidth;
        const thumbWidth = Math.max(20, ratio * trackWidth);
        const maxScroll = scrollWidth - clientWidth;
        const scrollRatio = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
        const maxThumbLeft = trackWidth - thumbWidth;

        thumb.style.width = `${thumbWidth}px`;
        thumb.style.left = `${scrollRatio * maxThumbLeft}px`;
      };

      const showRestState = () => {
        updateThumb();
        track.style.opacity = '1';
        thumb.style.opacity = '1';
      };

      syncPosition();
      requestAnimationFrame(showRestState);
      const t1 = setTimeout(showRestState, 200);
      const t2 = setTimeout(showRestState, 1000);

      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;

      const onScroll = () => {
        requestAnimationFrame(updateThumb);
      };
      scroller.addEventListener('scroll', onScroll);

      let mutationRafId = 0;
      const mutationObserver = new MutationObserver(() => {
        cancelAnimationFrame(mutationRafId);
        mutationRafId = requestAnimationFrame(updateThumb);
      });
      mutationObserver.observe(scrollerContent, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });

      const onThumbMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        isDragging = true;
        startX = e.clientX;
        startScrollLeft = scroller.scrollLeft;
        thumb.style.cursor = 'grabbing';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      const onMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        const dx = e.clientX - startX;
        const trackWidth = track.clientWidth;
        const scrollWidth = scroller.scrollWidth;
        const clientWidth = scroller.clientWidth;
        const ratio = clientWidth / scrollWidth;
        const thumbWidth = Math.max(20, ratio * trackWidth);
        const maxThumbLeft = trackWidth - thumbWidth;
        const maxScroll = scrollWidth - clientWidth;
        scroller.scrollLeft = startScrollLeft + (dx / maxThumbLeft) * maxScroll;
      };

      const onMouseUp = () => {
        isDragging = false;
        thumb.style.cursor = 'grab';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      thumb.addEventListener('mousedown', onThumbMouseDown);

      const onTrackClick = (e: MouseEvent) => {
        if (e.target === thumb) return;
        const rect = track.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const trackWidth = track.clientWidth;
        const scrollWidth = scroller.scrollWidth;
        const clientWidth = scroller.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        scroller.scrollLeft = (clickX / trackWidth) * maxScroll;
      };
      track.addEventListener('click', onTrackClick);

      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateThumb);
      });
      resizeObserver.observe(scroller);
      resizeObserver.observe(scrollerContent);
      resizeObserver.observe(container);

      teardown = () => {
        clearTimeout(t1);
        clearTimeout(t2);
        cancelAnimationFrame(mutationRafId);
        mutationObserver.disconnect();
        scroller.removeEventListener('scroll', onScroll);
        thumb.removeEventListener('mousedown', onThumbMouseDown);
        track.removeEventListener('click', onTrackClick);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        resizeObserver.disconnect();
        clipWrapper.remove();
        if (originalPosition === 'static') {
          container.style.position = '';
        }
      };

      return true;
    };

    if (!init()) {
      const waitObserver = new MutationObserver(() => {
        if (init()) {
          waitObserver.disconnect();
        }
      });
      waitObserver.observe(container, { childList: true, subtree: true });
      return () => {
        waitObserver.disconnect();
        teardown?.();
      };
    }

    return () => teardown?.();
  }, [
    containerRef,
    dataGridKey,
    enabled,
    theme.palette.scrollbar.thumb,
    theme.palette.scrollbar.track,
  ]);
};

export default useDataGridScrollbars;
